import styled, { css } from "styled-components";
import { motion } from "framer-motion";
import {
  MetricCard,
  MetricLabel,
  MetricValue,
  PageContainer,
  PageState as SharedPageState,
  PageTitle as SharedPageTitle,
  SectionTitle as SharedSectionTitle,
  getToneTokens,
} from "../../styles/shared";

export const Wrapper = styled(PageContainer)``;

export const PageState = styled(SharedPageState)``;

export const PageTitle = styled(SharedPageTitle)`
  margin-bottom: 20px;
`;

export const SectionTitle = styled(SharedSectionTitle)`
  margin: 28px 0 12px;
`;

export const KpiRow = styled.div`
  display: grid;
  grid-template-columns: minmax(min(100%, 220px), 1fr) repeat(3, minmax(min(100%, 220px), 1fr));
  gap: 12px;

  @media (max-width: 1080px) {
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
  }
`;

export const KpiCard = styled(MetricCard)`
  min-height: 108px;
`;

export const KpiStack = styled.div`
  display: grid;
  gap: 12px;
  align-content: start;
`;

export const KpiLabel = styled(MetricLabel)``;

export const KpiValue = styled(MetricValue)``;

export const PainelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
  gap: 12px;
`;

export const PainelCard = styled(motion.button)`
  background: ${({ theme }) => theme.surface};
  border: 1px solid
    ${({ theme, $hasAlert }) => ($hasAlert ? theme.dangerBorder : theme.border)};
  border-radius: 12px;
  padding: 18px;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  position: relative;
  text-align: left;
  min-height: 92px;
`;

export const PainelIcon = styled.div`
  color: ${({ theme }) => theme.accent};
  flex-shrink: 0;
`;

export const PainelInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const PainelLabel = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.textPrimary};
  overflow-wrap: anywhere;
`;

export const PainelTotal = styled.div`
  font-size: 0.82rem;
  color: ${({ theme }) => theme.textSecondary};
  margin-top: 4px;
  overflow-wrap: anywhere;
`;

export const AlertBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  font-weight: 600;
  color: ${({ theme, $active }) => ($active ? theme.dangerText : theme.textSecondary)};
  background: ${({ theme, $active }) => ($active ? theme.dangerSurface : theme.surfaceAlt)};
  border: 1px solid ${({ theme, $active }) => ($active ? theme.dangerBorder : theme.border)};
  padding: 4px 8px;
  border-radius: 999px;
  flex-shrink: 0;
  margin-top: 8px;
`;

/* =============================================================
 *  Redesign Home — TOC Command Panel
 * ============================================================= */

export const Greeting = styled.div`
  margin-top: -8px;
  margin-bottom: 14px;
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
  text-transform: capitalize;
`;

export const CommandGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 22px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const cmdCardCss = css`
  ${({ theme, $tone = "default" }) => {
    const tokens = getToneTokens(theme, $tone);
    return css`
      background: ${tokens.background};
      border: 1px solid ${tokens.border};
      color: ${tokens.text};
    `;
  }}
  border-radius: 14px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  min-height: 140px;
`;

export const CmdCard = styled.section`
  ${cmdCardCss}
`;

export const CmdCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.85;
`;

export const CmdCardTitle = styled.div`
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.15;
`;

export const CmdCardSub = styled.div`
  font-size: 13px;
  line-height: 1.4;
  opacity: 0.85;
`;

export const CmdCardMetrics = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 18px;
  margin-top: 4px;
`;

export const CmdCardMetric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;

  span:first-child {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    opacity: 0.75;
  }
  span:last-child {
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.01em;
  }
`;

export const CmdCardCta = styled.button`
  align-self: flex-start;
  margin-top: auto;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid currentColor;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: var(--surface-hover);
  }
`;

export const CalibracaoBanner = styled.button`
  align-self: flex-start;
  margin-top: 2px;
  padding: 4px 8px;
  border-radius: 6px;
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textSecondary};
  font: inherit;
  font-size: 11px;
  border: 1px dashed ${({ theme }) => theme.border};
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.accent};
    color: ${({ theme }) => theme.textPrimary};
  }
`;

/* Buffers card */
export const BuffersBody = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  align-items: center;
  gap: 16px;
  flex: 1 1 auto;

  @media (max-width: 540px) {
    grid-template-columns: 100px 1fr;
    gap: 12px;
  }
`;

export const BuffersLegend = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 4px;
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const BuffersLegendItem = styled.li`
  display: grid;
  grid-template-columns: 12px 1fr auto;
  align-items: center;
  gap: 8px;
  font-size: 12px;
`;

export const BuffersDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

export const BuffersDotLabel = styled.span`
  color: inherit;
  opacity: 0.85;
`;

export const BuffersDotCount = styled.span`
  font-weight: 700;
`;

/* Throughput card */
export const SparklineWrap = styled.div`
  height: 36px;
  width: 100%;
  margin-top: 4px;
`;

export const MetaBar = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: ${({ theme }) => theme.border};
  overflow: hidden;
`;

export const MetaFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => `${Math.min(100, Math.max(0, ($pct || 0) * 100))}%`};
  background: ${({ theme, $tone = "success" }) => getToneTokens(theme, $tone).border};
  transition: width 0.3s ease;
`;

/* Fluxo TOC bar */
export const FluxoSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 22px;
`;

export const FluxoBar = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols || "repeat(6, 1fr)"};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  overflow: hidden;
  background: ${({ theme }) => theme.surface};
  min-height: 88px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

export const FluxoSegment = styled.button`
  position: relative;
  border: 0;
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  padding: 12px 10px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  text-align: left;
  font: inherit;

  & + & {
    border-left: 1px solid ${({ theme }) => theme.border};
  }

  @media (max-width: 760px) {
    & + & {
      border-left: 0;
      border-top: 1px solid ${({ theme }) => theme.border};
    }
  }

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }

  ${({ $bottleneck, theme }) =>
    $bottleneck &&
    css`
      box-shadow: inset 0 -3px 0 ${theme.accent};
      background: ${theme.surfaceAlt};
    `}
`;

export const FluxoLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.textSecondary};
`;

export const FluxoCount = styled.div`
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1;
`;

export const FluxoSub = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.textSecondary};
`;

export const GargaloFlag = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  background: ${({ theme }) => theme.accent};
  color: var(--ink-on-accent);
  padding: 2px 6px;
  border-radius: 4px;
`;

/* Criticos */
export const CriticosSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 22px;
`;

export const CriticosList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  overflow: hidden;
  background: ${({ theme }) => theme.surface};
`;

export const CriticoRow = styled.button`
  display: grid;
  grid-template-columns: 72px 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border: 0;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font: inherit;
  cursor: pointer;
  text-align: left;
  width: 100%;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }
`;

export const CriticoZona = styled.span`
  ${({ theme, $tone = "default" }) => {
    const tokens = getToneTokens(theme, $tone);
    return css`
      background: ${tokens.background};
      border: 1px solid ${tokens.border};
      color: ${tokens.text};
    `;
  }}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
`;

export const CriticoIdent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  strong {
    font-size: 13px;
    font-weight: 700;
    color: ${({ theme }) => theme.textPrimary};
  }
  span {
    font-size: 12px;
    color: ${({ theme }) => theme.textSecondary};
  }
`;

export const CriticoMetrica = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;

  strong {
    font-size: 14px;
    font-weight: 700;
  }
  span {
    font-size: 11px;
    color: ${({ theme }) => theme.textSecondary};
  }
`;

export const CriticosEmpty = styled.div`
  padding: 14px 16px;
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
  font-style: italic;
  background: ${({ theme }) => theme.surface};
  border: 1px dashed ${({ theme }) => theme.border};
  border-radius: 10px;
`;

/* Atalhos por etapa */
export const AtalhosSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-bottom: 12px;
`;

export const AtalhosGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const AtalhosGroupTitle = styled.h3`
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textSecondary};
`;

export const PainelCardBottleneck = styled(motion.button)`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.accent};
  border-radius: 12px;
  padding: 18px;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  position: relative;
  text-align: left;
  min-height: 92px;
  box-shadow: 0 0 0 1px ${({ theme }) => theme.accent}33;
`;

export const PainelGargaloFlag = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  background: ${({ theme }) => theme.accent};
  color: var(--ink-on-accent);
  padding: 2px 6px;
  border-radius: 4px;
`;

/* Section header */
export const SectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;
